/**
 * Role — the tenant's role catalog. Schema factory plus the entity's write
 * wiring; the read gate is the product's, per `membershipEntity`.
 *
 * `permission` is the product's OWN catalog schema, not a shared token list.
 * The two consumers' catalogs are different by design — 12 wildcard-free
 * `tickets.*` tokens against 25 wildcard-bearing `workspaces.*`/`owl.*` ones —
 * and a shared catalog would be a shared product decision, which is the one
 * thing this package must never take. Passing the schema rather than a string
 * union also means an unknown token cannot be stored: the parse happens at this
 * seam, which is what makes `permissionsContain`'s wildcard degeneracy sound.
 */
import type { UserBase } from "@mhmo91/ican";
import { z } from "zod";
import type { MembershipActor } from "./actor.ts";
import type { EmptyShape } from "./entity.ts";
import {
	type EntityFactory,
	membershipEntity,
	type ReadPolicy,
} from "./entity.ts";

export const ROLE_ACTIONS = [
	"read",
	"list",
	"create",
	"update",
	"delete",
] as const;

export function createRoleSchema<
	P extends string,
	T extends z.ZodRawShape = EmptyShape,
>(opts: { readonly permission: z.ZodType<P>; readonly productFields?: T }) {
	return z
		.object({
			name: z.string().min(1),
			permissions: z.array(opts.permission).min(1),
			isBuiltIn: z.boolean(),
		})
		.extend((opts.productFields ?? {}) as T);
}

export function createRoleEntity<
	User extends UserBase & MembershipActor,
	Schema extends z.ZodTypeAny,
>(
	makeEntity: EntityFactory<User>,
	opts: {
		readonly schema: Schema;
		readonly namespace: string;
		readonly path: string;
		readonly hierarchicalGrants?: boolean;
		readonly handlers: {
			readonly create: string;
			readonly update: string;
			readonly delete: string;
		};
		readonly readPolicy: ReadPolicy<User, typeof ROLE_ACTIONS, Schema>;
	},
) {
	return membershipEntity<User, typeof ROLE_ACTIONS, Schema>(makeEntity, {
		schema: opts.schema,
		actions: ROLE_ACTIONS,
		namespace: opts.namespace,
		path: opts.path,
		...(opts.hierarchicalGrants === undefined
			? {}
			: { hierarchicalGrants: opts.hierarchicalGrants }),
		handlers: opts.handlers,
		readPolicy: opts.readPolicy,
	});
}
